import { ReactNode, useMemo } from 'react';

import {
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Text,
} from '@chakra-ui/react';

import { DatePickerInput, DropdownInput } from '@aslanpay/portal-common-lib';

import {
  getScheduleTimeSlots,
  getUkToday,
  isRealUkTime,
  isSlotInFuture,
  toDisplayScheduleDate,
} from 'app/utils/scheduledGifts';

import { SendMode, SendTimingFieldsProps } from './types';

function SendModeOption({
  title,
  description,
  value,
  sendMode,
  onSelect,
  children = null,
}: {
  title: string;
  description: string;
  value: SendMode;
  sendMode: SendMode;
  onSelect: (mode: SendMode) => void;
  children?: ReactNode;
}) {
  return (
    <Flex
      direction="row"
      gap="16px"
      justifyContent="start"
      alignItems="start"
      onClick={() => onSelect(value)}
      data-testid={`send-mode-${value.toLowerCase()}-option`}
    >
      <Radio
        value={value}
        mt="2px"
        isChecked={sendMode === value}
        onChange={() => onSelect(value)}
      />
      <Flex direction="column" gap="8px" flex={1} cursor="pointer">
        <Text as="p" textStyle="h6Regular">
          {title}
        </Text>
        <Text as="small" textStyle="regular" userSelect="none">
          {description}
        </Text>
        {children}
      </Flex>
    </Flex>
  );
}

function SendTimingFields({
  sendMode,
  onSendModeChange,
  scheduleDate,
  onScheduleDateChange,
  scheduleTime,
  onScheduleTimeChange,
  dateError,
  timeError,
  canPostAsRecognition,
  postAsRecognition,
  onPostAsRecognitionChange,
  isScheduleOnly = false,
}: SendTimingFieldsProps) {
  // Slots already gone on the chosen day, or skipped as the clocks go forward, can't be picked.
  const timeOptions = useMemo(
    () =>
      getScheduleTimeSlots().map((slot) => ({
        value: slot,
        text: slot,
        isDisabled:
          !!scheduleDate &&
          (!isSlotInFuture(scheduleDate, slot) || !isRealUkTime(scheduleDate, slot)),
      })),
    [scheduleDate]
  );

  const scheduleFields = (
    <Flex direction="column" gap="16px" onClick={(event) => event.stopPropagation()}>
      <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap="12px">
        <FormControl isRequired isInvalid={!!dateError}>
          <FormLabel>Date</FormLabel>
          <DatePickerInput
            placeholder="Select a date"
            defaultValue={toDisplayScheduleDate(scheduleDate)}
            onChange={onScheduleDateChange}
            onBlur={() => {}}
            // Not disablePast, which also blocks today: a gift can go out later the same day.
            minDate={getUkToday()}
          />
          <FormErrorMessage>{dateError}</FormErrorMessage>
        </FormControl>
        <FormControl isRequired isInvalid={!!timeError}>
          <FormLabel>Time (UK)</FormLabel>
          <DropdownInput
            data-testid="schedule-time-dropdown"
            placeholder="Select a time"
            options={timeOptions}
            defaultValue={scheduleTime ? { value: scheduleTime, text: scheduleTime } : undefined}
            onChange={(value) => onScheduleTimeChange(value.toString())}
            isSearchable
          />
          <FormErrorMessage>{timeError}</FormErrorMessage>
        </FormControl>
      </Grid>
      {canPostAsRecognition && (
        <Checkbox
          data-testid="post-as-recognition-checkbox"
          isChecked={postAsRecognition}
          onChange={(event) => onPostAsRecognitionChange(event.target.checked)}
        >
          <Text textStyle="smallMedium">Also post as a recognition</Text>
        </Checkbox>
      )}
    </Flex>
  );

  if (isScheduleOnly) {
    return scheduleFields;
  }

  return (
    <RadioGroup value={sendMode} data-testid="send-mode-radio-group">
      <Flex direction="column" gap="20px">
        <SendModeOption
          title="Send now"
          description="Employees receive the points straight away."
          value="NOW"
          sendMode={sendMode}
          onSelect={onSendModeChange}
        />
        <SendModeOption
          title="Schedule for later"
          description="Pick a date and time. Points leave the company balance when the gift is sent."
          value="SCHEDULE"
          sendMode={sendMode}
          onSelect={onSendModeChange}
        >
          {sendMode === 'SCHEDULE' && scheduleFields}
        </SendModeOption>
      </Flex>
    </RadioGroup>
  );
}

export default SendTimingFields;
